// Quote Builder — SJB board colour combobox.
//
// A native <select> cannot hold a filter box, and 614 colours is too many to
// scroll. This is a button + popover: text filter on top, then the list in TWO
// clearly separated groups.
//
// ── Why two groups and not one sorted list ────────────────────────────────
// A discount and a "PLUS n" uplift are the same axis with opposite signs, so
// they sort as one continuous cheapest-first run. Rendering them that way is
// tempting and wrong: at the moment of selection a "PLUS 20" row sitting
// directly under "0%" reads as just another, slightly worse, discount tier.
// The two are separated under their own headings for the same reason the line
// badge says "45% board discount" rather than "45% off list" — an uplift must
// never be mistakable for a discount. Steve, 2026-09-04.
//
// Within each group the order is still cheapest carcass first: discounts run
// 45% → 0%, uplifts run PLUS 20 → PLUS 150. `priceRank` from the server
// already encodes exactly that, so both groups are one ascending sort.

import { escapeHtml } from "./formatters";
import type { SjbColour } from "./sjb-catalogue";

export interface ColourComboHandle {
  unmount(): void;
  /** Re-render against a new selection without remounting. */
  setValue(key: string): void;
}

export interface ColourComboOptions {
  colours: SjbColour[];
  selectedKey: string;
  /** Label above the control. */
  label: string;
  /** Distinguishes the quote-level strip from a per-line editor in the DOM. */
  role: string;
  onPick(colour: SjbColour): void;
  /**
   * FLAT mode — one A-Z list, no discount/uplift groups, no price tags.
   *
   * For front EDGING, which draws on the same 614 board decors but carries
   * none of their pricing: edge tape is a flat cost whatever the decor, so a
   * "45% off carcass" tag beside it would be a claim about a number that does
   * not apply. With the tags gone the two groups have nothing left to mean,
   * and price-rank ordering becomes arbitrary — so the list sorts by name,
   * which is how someone hunting a decor actually looks for one.
   */
  flat?: boolean;
  /** Shown on the button when nothing is selected. */
  placeholder?: string;
  /** A non-decor row pinned to the top and never filtered out — "Matching
   *  carcass" for edging, which is the default and is not a decor at all. */
  pinned?: { key: string; label: string };
  /** What the discount is off, for the row tags. "carcass" on a cabinet,
   *  where the adjustment misses the hardware; "list" on a panel, where the
   *  whole line is board. See colourTag(). */
  tagNoun?: string;
}

/**
 * "45% off carcass" / "+100% on carcass". Never a bare "% off" — on a cabinet
 * the board adjustment does not reach hinges or drawer boxes, so an unnamed
 * percentage would overstate it.
 *
 * `noun` exists because the same 614 colours now price TWO different things.
 * On an ABS panel the whole line is board, so "off carcass" names something
 * that is not on the line at all; the panel picker passes "list", which is
 * both true there and still not a bare percentage.
 */
export function colourTag(c: SjbColour, noun = "carcass"): string {
  if (c.kind === "uplift") return `+${Math.abs(c.discPct)}% on ${noun}`;
  if (c.kind === "discount") return `${c.discPct}% off ${noun}`;
  return "price on application";
}

const GROUPS: ReadonlyArray<{ kind: SjbColour["kind"]; heading: string; note: string }> = [
  { kind: "discount", heading: "Board discount", note: "carcass discounted" },
  { kind: "uplift",   heading: "Premium finishes — uplift", note: "carcass costs MORE" },
];

export function mountColourCombobox(
  host: HTMLElement,
  opts: ColourComboOptions,
): ColourComboHandle {
  let mounted = true;
  let open = false;
  let filter = "";
  let activeIdx = -1;
  let selectedKey = opts.selectedKey;

  const sorted = opts.flat
    ? [...opts.colours].sort((a, b) => a.label.localeCompare(b.label))
    : [...opts.colours].sort(
        (a, b) => (a.priceRank - b.priceRank) || a.label.localeCompare(b.label),
      );

  /** Rows currently visible, in render order — the keyboard cursor indexes
   *  into this, so it always matches what is on screen. */
  /** The pinned row as a SjbColour, so it is an ordinary option everywhere —
   *  keyboard cursor, selection, filtering — rather than a special case each
   *  of those has to know about. `priceRank` 0 and kind "unreadable" keep it
   *  out of the grouped mode entirely; it is only ever used flat. */
  const pinnedRow: SjbColour | null = opts.pinned
    ? { key: opts.pinned.key, brand: "", colour: opts.pinned.label, structure: "",
        label: opts.pinned.label, group: "", kind: "unreadable", discPct: 0,
        priceRank: 0, search: opts.pinned.label.toLowerCase() }
    : null;

  function visible(): SjbColour[] {
    const f = filter.trim().toLowerCase();
    const match = f ? sorted.filter((c) => c.search.includes(f)) : sorted;
    if (opts.flat) {
      // Pinned first and never filtered out: it is the default, and hiding it
      // behind a search for a decor makes the way back to it disappear.
      return (pinnedRow ? [pinnedRow] : []).concat(match);
    }
    return GROUPS.flatMap((g) => match.filter((c) => c.kind === g.kind))
      .concat(match.filter((c) => c.kind === "unreadable"));
  }

  function selected(): SjbColour | undefined {
    if (pinnedRow && pinnedRow.key === selectedKey) return pinnedRow;
    return sorted.find((c) => c.key === selectedKey);
  }

  function rowsHtml(): string {
    const f = filter.trim().toLowerCase();
    const match = f ? sorted.filter((c) => c.search.includes(f)) : sorted;
    if (match.length === 0 && !(opts.flat && pinnedRow)) {
      return `<li class="sjb-combo__empty" role="presentation">No colour matches “${escapeHtml(filter)}”.</li>`;
    }
    let idx = 0;
    const chunks: string[] = [];

    // Flat: one A-Z list, no group headers, no price tags. `idx` must count
    // the pinned row too, or the keyboard cursor and the rendered list drift
    // apart by one.
    if (opts.flat) {
      const row = (c: SjbColour): string => {
        const i = idx++;
        return `<li class="sjb-combo__opt${c.key === selectedKey ? " is-selected" : ""}${i === activeIdx ? " is-active" : ""}"
                    role="option" aria-selected="${c.key === selectedKey}"
                    id="${escapeHtml(opts.role)}-opt-${i}" data-key="${escapeHtml(c.key)}" data-i="${i}">
                  <span class="sjb-combo__opt-label">${escapeHtml(c.label)}</span>
                </li>`;
      };
      if (pinnedRow) chunks.push(row(pinnedRow));
      if (match.length === 0) {
        chunks.push(`<li class="sjb-combo__empty" role="presentation">No decor matches “${escapeHtml(filter)}”.</li>`);
      } else {
        for (const c of match) chunks.push(row(c));
      }
      return chunks.join("");
    }

    for (const g of GROUPS) {
      const rows = match.filter((c) => c.kind === g.kind);
      if (rows.length === 0) continue;
      chunks.push(
        `<li class="sjb-combo__group" role="presentation">
           <span class="sjb-combo__group-name">${escapeHtml(g.heading)}</span>
           <span class="sjb-combo__group-note">${escapeHtml(g.note)}</span>
           <span class="sjb-combo__group-count">${rows.length}</span>
         </li>`,
      );
      for (const c of rows) {
        const i = idx++;
        chunks.push(
          `<li class="sjb-combo__opt${c.key === selectedKey ? " is-selected" : ""}${i === activeIdx ? " is-active" : ""}"
               role="option" aria-selected="${c.key === selectedKey}"
               id="${escapeHtml(opts.role)}-opt-${i}" data-key="${escapeHtml(c.key)}" data-i="${i}">
             <span class="sjb-combo__opt-label">${escapeHtml(c.label)}</span>
             <span class="sjb-combo__opt-tag sjb-combo__opt-tag--${c.kind}">${escapeHtml(colourTag(c, opts.tagNoun))}</span>
           </li>`,
        );
      }
    }
    return chunks.join("");
  }

  function paint(): void {
    if (!mounted) return;
    const cur = selected();
    const listId = `${opts.role}-list`;
    host.innerHTML = `
      <div class="sjb-combo" data-role="${escapeHtml(opts.role)}">
        <label class="qb-session-lbl" id="${escapeHtml(opts.role)}-lbl">${escapeHtml(opts.label)}</label>
        <button type="button" class="sjb-combo__button" data-role="combo-button"
                aria-haspopup="listbox" aria-expanded="${open}"
                aria-labelledby="${escapeHtml(opts.role)}-lbl">
          <span class="sjb-combo__value">${cur ? escapeHtml(cur.label) : escapeHtml(opts.placeholder ?? "— Select board colour —")}</span>
          ${cur && !opts.flat ? `<span class="sjb-combo__tag sjb-combo__tag--${cur.kind}">${escapeHtml(colourTag(cur, opts.tagNoun))}</span>` : ""}
          <svg class="sjb-combo__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>
        ${open ? `
        <div class="sjb-combo__pop">
          <div class="sjb-combo__search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"
                 stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/>
            </svg>
            <input type="text" data-role="combo-filter" autocomplete="off" spellcheck="false"
                   placeholder="Filter by name, brand or code…" value="${escapeHtml(filter)}"
                   role="combobox" aria-expanded="true" aria-controls="${listId}"
                   aria-activedescendant="${activeIdx >= 0 ? `${escapeHtml(opts.role)}-opt-${activeIdx}` : ""}" />
          </div>
          <ul class="sjb-combo__list" id="${listId}" role="listbox" data-role="combo-list">${rowsHtml()}</ul>
        </div>` : ""}
      </div>`;
    wire();
    if (open) {
      host.querySelector<HTMLInputElement>("[data-role='combo-filter']")?.focus();
      revealActive();
    }
  }

  /**
   * Repaint ONLY the option list.
   *
   * Typing used to call the full paint(), which replaces host.innerHTML and so
   * destroys the very input being typed into. The recreated input carries the
   * filter as a value ATTRIBUTE, and focusing it puts the caret at position 0
   * — so the second keystroke landed BEFORE the first ("c" then "r" gave
   * "rc"), matched nothing, and the box looked broken after one character.
   *
   * Leaving the input alone also preserves the caret, any selection, IME
   * composition and undo history, none of which survive an innerHTML swap.
   */
  function paintList(): void {
    if (!mounted || !open) return;
    const list = host.querySelector<HTMLElement>("[data-role='combo-list']");
    if (!list) { paint(); return; }
    list.innerHTML = rowsHtml();
    const input = host.querySelector<HTMLInputElement>("[data-role='combo-filter']");
    input?.setAttribute("aria-activedescendant",
      activeIdx >= 0 ? `${opts.role}-opt-${activeIdx}` : "");
    wireOptions();
    revealActive();
  }

  function revealActive(): void {
    // Guarded: scrollIntoView is absent in non-browser DOM implementations,
    // and keeping the active row visible is a nicety, not a correctness
    // requirement — it must never break the popover.
    const active = host.querySelector<HTMLElement>(".sjb-combo__opt.is-active");
    if (active && typeof active.scrollIntoView === "function") {
      active.scrollIntoView({ block: "nearest" });
    }
  }

  function wireOptions(): void {
    host.querySelectorAll<HTMLElement>(".sjb-combo__opt").forEach((el) => {
      el.addEventListener("click", () => { const k = el.dataset["key"]; if (k) choose(k); });
    });
  }

  function choose(key: string): void {
    const c = sorted.find((x) => x.key === key);
    if (!c) return;
    selectedKey = key;
    open = false;
    filter = "";
    activeIdx = -1;
    paint();
    opts.onPick(c);
  }

  function wire(): void {
    host.querySelector<HTMLButtonElement>("[data-role='combo-button']")
      ?.addEventListener("click", () => {
        open = !open;
        filter = "";
        // Cursor starts on the current selection so Enter is a no-op rather
        // than silently jumping to the top of the list.
        activeIdx = open ? Math.max(0, visible().findIndex((c) => c.key === selectedKey)) : -1;
        paint();
      });

    const input = host.querySelector<HTMLInputElement>("[data-role='combo-filter']");
    input?.addEventListener("input", (e) => {
      filter = (e.target as HTMLInputElement).value;
      activeIdx = visible().length > 0 ? 0 : -1;
      paintList();
    });
    input?.addEventListener("keydown", (e) => {
      const rows = visible();
      const k = (e as KeyboardEvent).key;
      if (k === "ArrowDown" || k === "ArrowUp") {
        e.preventDefault();
        if (rows.length === 0) return;
        activeIdx = k === "ArrowDown"
          ? (activeIdx + 1) % rows.length
          : (activeIdx - 1 + rows.length) % rows.length;
        paintList();
      } else if (k === "Enter") {
        e.preventDefault();
        const pick = rows[activeIdx];
        if (pick) choose(pick.key);
      } else if (k === "Escape") {
        e.preventDefault();
        open = false; filter = ""; activeIdx = -1;
        paint();
        host.querySelector<HTMLButtonElement>("[data-role='combo-button']")?.focus();
      }
    });

    wireOptions();
  }

  paint();
  return {
    unmount(): void { mounted = false; host.innerHTML = ""; },
    setValue(key: string): void { selectedKey = key; paint(); },
  };
}
